// Le bloc « Impression » du detail d'une commande, et le recapitulatif qui
// precede l'envoi chez Print.com.
//
// LE NAVIGATEUR NE CALCULE RIEN. Aucun groupe, aucun prix, aucun refus n'est
// fabrique ici : tout vient de la porte print-commander (action "devis"), et
// l'ecran AFFICHE. grouper() de src/impression.js n'est volontairement PAS
// appele : deux calculs pourraient diverger, et le recapitulatif approuve par
// Simon ne serait plus ce qui part a l'impression.
//
// 🧺 LE PANIER. Une impression couvre PLUSIEURS commandes. D'ou trois choses :
//   - le rattachement vit dans la table de liens impressions_commandes, qui EST
//     le verrou (decision D4). impressions.commande_id n'existe plus ;
//   - le recapitulatif prend une LISTE de commandes, et les groupes y sont
//     ranges par client ;
//   - les trois sorties de l'enfermement (section 3 bis de la spec) sont
//     designees par l'impression_id, JAMAIS par les cases cochees aujourd'hui :
//     les commandes d'un panier verrouille n'ont plus de case.
//
// 🪤 LE DOUBLE MONTAGE. DetailCommandeModal.js rend InfosContent DEUX FOIS
// (bureau et telephone) et src/index.css ne bascule que `display` : les deux
// sous-arbres sont TOUJOURS dans le document. D'ou le decoupage :
//
//   - useImpression()      : la lecture de la table de liens. Appelee UNE
//                            SEULE FOIS, depuis DetailCommandeModal.
//   - useDevisImpression() : le devis. Appelee UNE SEULE FOIS, au MEME endroit.
//                            ⚠️ JAMAIS depuis BlocImpression, monte deux fois :
//                            ce serait deux appels Print.com par ouverture.
//   - BlocImpression       : le resume + les boutons. Monte DEUX FOIS, donc
//                            AUCUN appel reseau au montage : tout lui arrive en
//                            props, et il n'appelle la porte qu'au CLIC.
//   - RecapImpression      : la fenetre de confirmation. Rendue UNE SEULE FOIS,
//                            et seulement quand elle est ouverte.
//
// 🕐 LE DEVIS EST PRECHARGE (Simon, 2026-10-03 : « je veux que la demande soit
// faite a chaque fois qu'on clique sur un plan valide, en arriere-plan ») —
// mais SEULEMENT dans le detail d'une commande. 🚫 Un PANIER n'est jamais
// precharge (section 4.1 de la spec) : son devis part au clic sur le bandeau.
// Trois regles tiennent ce prechargement honnete :
//   1. UN SEUL APPEL par ouverture : la cle en vol (enVol) et la memoire
//      bloquent tout doublon, y compris le double effet de React StrictMode.
//   2. LA MEMOIRE EST INVALIDEE par tout ce dont le devis depend : destination,
//      adresse, orientations des plans. Un devis perime ferait approuver a Simon
//      un recapitulatif qui ne correspond plus a ce qui part a l'impression.
//   3. UN ECHEC DE PRECHARGEMENT NE S'AFFICHE PAS. Le bloc ne recoit pas l'etat
//      du devis : rien ne change a l'ecran tant que Simon n'ouvre pas. Et comme
//      un echec n'est PAS mis en memoire, l'ouverture redemande.
//
// Les essais de ce fichier utilisent getAllByRole / getAllByText : getByRole
// leverait « found multiple elements » sur le bloc monte deux fois.
//
// Specification : docs/superpowers/specs/2026-10-04-panier-impression-design.md
// (sections 3 bis, 4.7 et 4.8). Plan :
// docs/superpowers/plans/2026-10-05-panier-impression.md (tache 6).
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../supabase";
import { ORIENTATIONS } from "../constants";
import VisuFichier from "./VisuFichier";

// ---------------------------------------------------------------------------
// Styles — repris de ReglagesImpression.js et PageReglages.js : une seule
// facon de dessiner une carte, un champ, un bouton. fontFamily: "inherit" est
// obligatoire sur tout champ et tout bouton, sinon le projet retombe en
// Times New Roman.
// ---------------------------------------------------------------------------
const CARTE = { background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: 16, marginBottom: 24 };
const TITRE = { fontSize: 15, fontWeight: 700, marginBottom: 12 };
const LIBELLE = { fontSize: 13, fontWeight: 600, color: "#374151" };
const AIDE = { fontSize: 12, color: "#9CA3AF" };
const CHAMP = {
  width: "100%", boxSizing: "border-box", marginTop: 4, padding: "8px 12px",
  borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 13,
  background: "#fff", color: "#374151", fontFamily: "inherit",
};
const BOUTON = {
  padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 13,
  background: "#fff", color: "#374151", cursor: "pointer", fontFamily: "inherit",
};
// 🪤 `border` en raccourci ET `borderColor` a cote : React previent
// « Removing a style property during rerender » des que le bouton bascule d'un
// style a l'autre (« Livrer chez moi » / « chez le client »), et la couleur de
// bordure peut rester celle d'avant. Un seul des deux, donc : le raccourci.
const BOUTON_PLEIN = { ...BOUTON, background: "#122131", border: "1px solid #122131", color: "#fff" };
const BOUTON_ETEINT = { ...BOUTON, cursor: "not-allowed", color: "#9CA3AF" };
// Grille qui ne deborde jamais : chaque colonne vaut au plus 1fr, donc a 375 px
// il n'en reste qu'une et les champs s'empilent tout seuls, sans regle @media.
// Le cadre neutre des messages. overflowWrap: "anywhere" parce qu'un refus de
// Print.com est un JSON d'un seul tenant : sans ca il deborde sur le cote a
// 375 px.
const CADRE = {
  marginTop: 12, padding: 12, background: "#F3F4F6", border: "1px solid #E5E7EB",
  borderRadius: 8, color: "#374151", fontSize: 13,
  overflowWrap: "anywhere", whiteSpace: "pre-wrap",
};
// Le rouge du projet (DetailCommandeModal.js, NotesSection). Un echec et un
// refus doivent se voir.
const ROUGE = "#DC2626";
const CADRE_ROUGE = { ...CADRE, background: "#FEF2F2", border: `1px solid #FECACA`, color: ROUGE };
const SUITE = { marginTop: 4 };
const RANGEE_BOUTONS = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 12 };

// Les neuf etats de la table impressions (migration 20261002120000), en court.
// Titres de colonnes courts, precisions en info-bulle (regle n. 3).
const ETATS = {
  envoi_en_cours: "Envoi en cours",
  commande: "Commandé",
  controle_fichier: "Contrôle du fichier",
  en_production: "En production",
  expedie: "Expédié",
  livre: "Livré",
  refuse: "Refusé",
  annule: "Annulé",
  echec: "Échec",
};

// Le MEME delai que la porte (DELAI_RENVOI_MS de print-commander) : apres dix
// minutes sans numero, un envoi n'est plus un envoi, c'est une panne. Avant,
// aucun bouton : un envoi qui dure trente secondes n'est pas une panne, et la
// porte refuserait de toute facon.
const DELAI_RENVOI_MS = 10 * 60 * 1000;

// ⬜ HT ou TTC n'est PAS tranche. La tache 5 le tranchera en comparant au
// panier print.com. Tant que ce n'est pas prouve, l'ecran ne l'affirme pas :
// une mention fausse sur le seul ecran ou Simon approuve une depense serait
// pire que pas de mention. Un seul endroit a changer.
// Le prix renvoye par Print.com est HORS TAXES. Tranche le 2026-10-02 par Simon,
// qui l'a compare a son panier print.com.
//
// TVA a 0 % : Print.com est neerlandais, et entre deux entreprises de l'Union la
// taxe est AUTOLIQUIDEE — le fournisseur facture 0 %, et c'est Simon qui la
// declare. Le TTC egale donc le HT, et l'afficher deux fois ne dirait rien.
// Si leur facturation change un jour : un seul chiffre a modifier ici, et le
// montant TTC apparaitra tout seul a cote du HT.
const TVA_POURCENTAGE = 0;
const MENTION_TAXE = TVA_POURCENTAGE === 0
  ? "TVA 0 % (autoliquidation)"
  : `TVA ${TVA_POURCENTAGE} %`;

const texte = (v) => (v === null || v === undefined ? "" : String(v));

// Deux decimales, virgule francaise. Pas d'Intl : le resultat doit etre le
// meme dans le navigateur et dans les essais.
function euros(montant) {
  const n = Number(montant);
  if (!Number.isFinite(n)) return "";
  return `${n.toFixed(2).replace(".", ",")} €`;
}

// Le montant tel que Simon veut le lire : « 7,95 € HT », et « 7,95 € HT
// (9,54 € TTC) » le jour ou une TVA s'appliquerait. A 0 % on n'ecrit pas deux
// fois le meme nombre.
function eurosHT(montant) {
  const n = Number(montant);
  if (!Number.isFinite(n)) return "";
  const ht = `${euros(n)} HT`;
  if (TVA_POURCENTAGE === 0) return ht;
  return `${ht} (${euros(n * (1 + TVA_POURCENTAGE / 100))} TTC)`;
}

// « 3 commandes », « 1 commande ». Une seule facon de l'ecrire.
function compteCommandes(n) {
  return `${n} commande${n > 1 ? "s" : ""}`;
}

// Une date ISO (AAAA-MM-JJ) telle que Print.com la renvoie, lue comme Simon la
// lit : « 08/10/2026 ». Pas de new Date ni d'Intl — une date nue lue comme un
// instant glisse d'un jour selon le fuseau, et le resultat doit etre le meme
// dans le navigateur et dans les essais. Ce qui n'est pas une date ISO est
// reaffiche tel quel : jamais une date inventee.
function dateFr(valeur) {
  const s = texte(valeur).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

// Depuis quand l'impression est-elle dans cet etat ? « Depuis » = depuis la
// derniere ecriture, exactement comme la porte. Une date illisible donne 0 :
// on n'ouvre jamais une sortie sur une date qu'on n'a pas su lire.
function ageMs(impression) {
  const quand = Date.parse(texte(impression?.maj_le) || texte(impression?.cree_le));
  if (!Number.isFinite(quand)) return 0;
  return Date.now() - quand;
}

// Nomme un plan comme la porte le nomme (nommerPlan dans print-commander) :
// « Plan 2 (Hall) ». Le numero du plan est vide sur presque toutes les
// commandes, on compte donc a partir de 1, comme l'ecran les affiche.
// `prefixe` porte la reference de la commande, et SEULEMENT dans un panier
// (decision D3) : dans le detail d'une commande, Simon sait deja laquelle il
// regarde, et la prefixer serait du bruit.
function nommerPlan(plan, index, prefixe) {
  const emplacement = texte(plan && plan.emplacement).trim();
  const nom = `Plan ${index + 1}`;
  const avecLieu = emplacement ? `${nom} (${emplacement})` : nom;
  return prefixe ? `${prefixe} · ${avecLieu}` : avecLieu;
}

// Le nom du client, calcule comme la porte le calcule (section 4.2 de la spec).
function nomDuClient(commande) {
  return texte(commande?.client_societe).trim()
    || `${texte(commande?.client_prenom).trim()} ${texte(commande?.client_nom).trim()}`.trim();
}

// Une icone SVG, trait fin, currentColor, meme taille. Regle n. 2 : zero emoji.
function Icone({ chemin, titre }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden={titre ? undefined : "true"} role={titre ? "img" : undefined}
      style={{ flexShrink: 0 }}>
      {titre ? <title>{titre}</title> : null}
      <path d={chemin} />
    </svg>
  );
}

const TRAIT_IMPRIMANTE = "M6 9V4h12v5M6 18H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-1M6 14h12v6H6z";
const TRAIT_ALERTE = "M12 9v4m0 3h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z";

// ===========================================================================
// La lecture de l'impression — UNE SEULE FOIS, par DetailCommandeModal
// ===========================================================================

const COLONNES = "id, etat, motif, numero_printcom, environnement, destination, cree_le, maj_le";

// `lu` dit « la table a VRAIMENT ete lue ». Sans lui, au tout premier rendu
// chargement vaut false et impression vaut null : on croirait savoir qu'il n'y a
// aucune ligne, et le prechargement du devis partirait meme sur une commande deja
// envoyee chez Print.com.
export function useImpression(commandeId, actif) {
  const [etat, setEtat] = useState({
    impression: null, nbCommandes: 0, chargement: false, erreur: "", lu: false,
  });
  const [tour, setTour] = useState(0);
  const relire = useCallback(() => setTour((t) => t + 1), []);

  useEffect(() => {
    // Pas admin, ou commande pas validee : AUCUN appel. L'ecran de detail est
    // ouvert par tout le monde, des dizaines de fois par jour.
    if (!actif || !commandeId) {
      setEtat({ impression: null, nbCommandes: 0, chargement: false, erreur: "", lu: false });
      return undefined;
    }
    let vivant = true;
    setEtat((e) => ({ ...e, chargement: true }));
    (async () => {
      // La commande -> son lien -> son impression. C'est la table de liens qui
      // porte desormais le rattachement (decision D4) : impressions.commande_id
      // n'existe plus, parce qu'un panier a plusieurs commandes.
      //
      // ⚠️ maybeSingle() et pas single() : l'immense majorite des commandes n'a
      // aucune impression, et single() traiterait ce cas normal comme une erreur.
      //
      // 🔴 ET AUCUN FILTRE D'ETAT. L'ancien code ecartait 'annule' et 'refuse'.
      // Avec la table de liens, LE LIEN EST LA VERITE : s'il existe, la commande
      // est verrouillee, quel que soit l'etat affiche. Garder l'ancien filtre
      // rendrait une impression 'annule' a liens survivants INVISIBLE, et son
      // bouton de reparation inatteignable.
      const { data, error } = await supabase
        .from("impressions_commandes")
        .select("impression_id, impressions (" + COLONNES + ")")
        .eq("commande_id", commandeId)
        .maybeSingle();
      if (!vivant) return;
      if (error) {
        setEtat({ impression: null, nbCommandes: 0, chargement: false, erreur: texte(error.message), lu: true });
        return;
      }
      // PostgREST rend un objet pour une relation « plusieurs vers un ». On
      // accepte quand meme le tableau : le jour ou la forme change, l'ecran ne
      // doit pas s'eteindre en silence.
      const brut = data && data.impressions ? data.impressions : null;
      const impression = Array.isArray(brut) ? (brut[0] ?? null) : brut;
      if (!impression) {
        setEtat({ impression: null, nbCommandes: 0, chargement: false, erreur: "", lu: true });
        return;
      }
      // Compte les commandes de CE panier. commandes_refs ferait aussi
      // l'affaire, mais les liens sont la verite vivante : commandes_refs est un
      // instantane de l'envoi.
      const { count, error: erreurCompte } = await supabase
        .from("impressions_commandes")
        .select("commande_id", { count: "exact", head: true })
        .eq("impression_id", impression.id);
      if (!vivant) return;
      // Un comptage rate ne doit PAS faire disparaitre l'impression : ce sont
      // les trois sorties de l'enfermement qui partiraient avec elle. On garde
      // la ligne, et l'ecran n'annonce simplement aucun nombre.
      const nbCommandes = erreurCompte ? 0 : (Number(count) > 0 ? Number(count) : 0);
      setEtat({ impression, nbCommandes, chargement: false, erreur: "", lu: true });
    })();
    return () => { vivant = false; };
  }, [commandeId, actif, tour]);

  return { ...etat, relire };
}

// ===========================================================================
// Le devis — UNE SEULE FOIS, par DetailCommandeModal, et en arriere-plan
// ===========================================================================

function idsDe(commandes) {
  return (Array.isArray(commandes) ? commandes : [])
    .map((c) => texte(c && c.id).trim())
    .filter((id) => id);
}

// LA CLE DE LA MEMOIRE : tout ce dont le devis depend, et rien d'autre.
//   - la cible : l'impression (un reessai) ou la liste des commandes ;
//   - la destination de CHAQUE commande (chez moi / chez le client) ;
//   - l'adresse de chacune, quand elle part chez son client ;
//   - l'orientation de chaque plan, qui re-decoupe les groupes et change les prix.
// Tant que ceux-la n'ont pas bouge, le devis en memoire reste vrai. Des que
// l'un change, la cle change, et un nouveau devis est demande.
// 🔴 L'ADRESSE N'ENTRE DANS LA CLE QUE POUR UNE COMMANDE QUI PART CHEZ SON
// CLIENT. En livraison au local la porte prend celle des Reglages : revenir sur
// « chez moi » apres avoir tape une adresse ne doit RIEN redemander. C'est la
// meme nuance que la signature d'entree de la porte, et elle se perdrait sans
// qu'aucun essai ne rougisse si on aplatissait tout par commande.
// Les cles de l'adresse sont TRIEES, et les commandes aussi : deux etats
// identiques ecrits dans un ordre different doivent donner la meme cle.
function cleDevis(choix, plansParCommande, cible) {
  const entrees = plansParCommande instanceof Map ? [...plansParCommande.entries()] : [];
  const orientations = entrees
    .map(([id, plans]) => [id, (Array.isArray(plans) ? plans : []).map((p) => texte(p && p.orientation))])
    .sort((a, b) => (a[0] < b[0] ? -1 : (a[0] > b[0] ? 1 : 0)));
  const destinations = (Array.isArray(choix) ? choix : [])
    .map((e) => (e && e.destination === "client"
      ? [texte(e.id), "client", Object.keys(e.adresse ?? {}).sort().map((k) => [k, texte(e.adresse[k])])]
      : [texte(e && e.id), "local"]))
    .sort((a, b) => (a[0] < b[0] ? -1 : (a[0] > b[0] ? 1 : 0)));
  return JSON.stringify([cible, destinations, orientations]);
}

// `impressionId` : le reessai (decision D5). Il GAGNE sur la liste des
// commandes — c'est l'impression qui designe l'ensemble, jamais les cases
// cochees aujourd'hui.
export function useDevisImpression(commandes, actif, impressionId = null) {
  const cleIds = idsDe(commandes).join(",");
  const impression = texte(impressionId).trim();
  // La cible, en une chaine : c'est elle qui vide la memoire quand elle change.
  const cible = impression ? `i:${impression}` : `c:${cleIds}`;
  const [etat, setEtat] = useState({ devis: null, attente: false, refusPorte: "" });
  // La memoire des devis de CETTE cible. Un useRef, pas un useState : la
  // remplir ne doit pas redessiner l'ecran.
  const memoire = useRef(new Map());
  // Un devis plus ancien ne doit jamais ecraser un plus recent.
  const sequence = useRef(0);
  // La cle de la demande EN VOL. C'est ce garde-fou qui garantit un seul appel
  // meme si quelque chose demandait deux fois la meme chose en meme temps —
  // le double effet de React StrictMode, par exemple.
  const enVol = useRef("");

  // On change de cible : la memoire d'un panier ne vaut rien pour un autre, et
  // tout devis en vol devient perime.
  // 🪤 SEULEMENT SUR UN VRAI CHANGEMENT. Au montage il n'y a rien a vider, et cet
  // effet s'executerait APRES celui du recapitulatif (les effets des enfants
  // partent avant ceux du parent) : il effacerait le devis qui vient de partir.
  const ciblePrecedente = useRef(cible);
  useEffect(() => {
    if (ciblePrecedente.current === cible) return;
    ciblePrecedente.current = cible;
    memoire.current.clear();
    enVol.current = "";
    sequence.current += 1;
    setEtat({ devis: null, attente: false, refusPorte: "" });
  }, [cible]);

  // « Fais en sorte qu'un devis existe pour cet etat-la. » N'appelle Print.com
  // que si c'est vraiment necessaire.
  const assurer = useCallback(async (choix, plansParCommande) => {
    if (!actif) return;
    if (!impression && !cleIds) return;
    const liste = Array.isArray(choix) ? choix : [];
    // Sans impression, une liste vide ne veut rien dire : la porte refuserait
    // « Aucune commande demandee ». On ne part pas pour rien.
    if (!impression && liste.length === 0) return;
    const cle = cleDevis(liste, plansParCommande, cible);
    // Deja en vol pour CETTE cle : surtout pas un deuxieme appel.
    if (enVol.current === cle) return;
    const memorise = memoire.current.get(cle);
    if (memorise) {
      // Reutilise : AUCUN appel reseau. Toute demande encore en vol est perimee.
      sequence.current += 1;
      enVol.current = "";
      setEtat({ devis: memorise, attente: false, refusPorte: "" });
      return;
    }
    const n = sequence.current + 1;
    sequence.current = n;
    enVol.current = cle;
    setEtat({ devis: null, attente: true, refusPorte: "" });
    // 🔴 Un reessai est designe par son IMPRESSION : la composition d'un panier
    // ne change JAMAIS sur un reessai (D5). Mais les DESTINATIONS, elles,
    // viennent du recapitulatif que Simon a sous les yeux.
    const corps = impression
      ? { action: "devis", impression_id: impression }
      : { action: "devis" };
    // 🔴 LA LISTE N'EST ENVOYEE QUE SI L'ECRAN LA CONNAIT ENTIEREMENT. La porte
    // exige qu'elle soit EXACTEMENT celle de ses liens, sinon elle refuse. Avant
    // le premier devis d'un reessai, l'ecran ne connait qu'une commande sur
    // trois : il n'envoie donc rien, et la porte applique « chez moi » partout
    // -- ce qui est justement l'etat de reouverture (decision E4).
    if (liste.length > 0) {
      corps.commandes = liste.map((e) => (e.destination === "client"
        // En livraison au local, aucune adresse n'est envoyee : la porte prend
        // celle des Reglages.
        ? { id: texte(e.id), destination: "client", adresse: e.adresse }
        : { id: texte(e.id), destination: "local" }));
    }
    let reponse;
    try {
      reponse = await supabase.functions.invoke("print-commander", { body: corps });
    } catch (e) {
      reponse = { data: null, error: e };
    }
    if (n !== sequence.current) return;
    if (enVol.current === cle) enVol.current = "";
    const { data, error } = reponse ?? {};
    // invoke() ne jette pas : il resout avec data: null des que la reponse n'est
    // pas 2xx. Troisieme cas, donc, et pas un seul.
    if (!data) {
      // Un echec n'est PAS un devis : il n'entre pas en memoire, pour qu'un
      // nouvel essai soit possible a l'ouverture du recapitulatif.
      setEtat({
        devis: null,
        attente: false,
        refusPorte: texte(error?.message) || "Réponse vide de print-commander.",
      });
      return;
    }
    memoire.current.set(cle, data);
    setEtat({ devis: data, attente: false, refusPorte: "" });
  }, [cleIds, impression, cible, actif]);

  return { ...etat, assurer };
}

// ===========================================================================
// Le bloc — monte DEUX FOIS. Aucun appel reseau AU MONTAGE : il n'appelle la
// porte qu'au clic, et un clic ne se produit que dans la copie visible.
// ===========================================================================

// L'avertissement de l'abandon. 🔴 C'est la PORTE qui choisit lequel s'affiche
// (champ appel_print_com) : elle seule a ecrit le motif, elle seule sait si un
// appel a pu partir. L'ecran AFFICHE, il ne deduit pas.
const ABANDON_DANGER = "Vérifiez d'abord chez Print.com qu'aucune commande n'a été créée."
  + " Si une commande existe là-bas, abandonner ici fera partir un deuxième exemplaire,"
  + " qui sera imprimé, livré et facturé.";
const ABANDON_SANS_APPEL = "Aucune commande n'a été envoyée à Print.com.";

// Garde dans l'enveloppe, hooks dans le composant interne : c'est le motif de
// ReglagesImpression.js. react-hooks/rules-of-hooks est une ERREUR dans ce
// projet, et CI=true transforme les avertissements en erreurs.
export default function BlocImpression(props) {
  // Commande validee, administrateur seulement. Sinon : rien du tout.
  if (props.estAdmin !== true || props.statut !== "Validé") return null;
  return <Bloc {...props} />;
}

function Bloc({ impression, nbCommandes, chargement, erreur, onOuvrir, onRelire }) {
  // Le premier temps de l'abandon, tel que la porte l'a renvoye. Tant qu'il
  // vaut null, aucune confirmation n'est a l'ecran.
  const [confirmation, setConfirmation] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const [messageSortie, setMessageSortie] = useState("");

  const titre = (
    <div style={{ ...TITRE, display: "flex", alignItems: "center", gap: 8 }}>
      <Icone chemin={TRAIT_IMPRIMANTE} />
      <span>Impression</span>
    </div>
  );

  // Un appel de sortie : abandonner, ou verifier. Jamais au montage.
  const appeler = async (corps) => {
    setOccupe(true);
    setMessageSortie("");
    let reponse;
    try {
      reponse = await supabase.functions.invoke("print-commander", { body: corps });
    } catch (e) {
      reponse = { data: null, error: e };
    }
    setOccupe(false);
    const { data, error } = reponse ?? {};
    if (!data) {
      return { ok: false, erreur: texte(error?.message) || "Réponse vide de print-commander." };
    }
    return data;
  };

  // 🔴 PREMIER TEMPS : la porte n'ecrit RIEN. Elle renvoie de quoi afficher le
  // BON avertissement. Sans `confirme: true`, un clic ne peut pas suffire.
  const demanderAbandon = async () => {
    const data = await appeler({ action: "abandonner", impression_id: texte(impression?.id) });
    if (data && data.confirmation_requise === true) {
      setConfirmation(data);
      return;
    }
    setMessageSortie(texte(data?.erreur) || "La porte a refusé l'abandon.");
  };

  // DEUXIEME TEMPS : le travail reel. Il n'existe que si Simon a lu
  // l'avertissement et accepte.
  const confirmerAbandon = async () => {
    const data = await appeler({
      action: "abandonner", impression_id: texte(impression?.id), confirme: true,
    });
    setConfirmation(null);
    if (data && data.ok === true) {
      // Les liens sont partis : la commande redevient cochable, et ce bloc
      // n'a plus d'impression a montrer.
      onRelire?.();
      return;
    }
    setMessageSortie(texte(data?.erreur) || "L'abandon a échoué.");
  };

  // La sortie 3 : on renvoie EXACTEMENT la meme demande, pour que Print.com la
  // reconnaisse. Rien n'est regroupe, rien n'est rechiffre.
  const verifierChezPrintCom = async () => {
    const data = await appeler({ action: "verifier", impression_id: texte(impression?.id) });
    if (data && data.ok === true) {
      onRelire?.();
      return;
    }
    setMessageSortie(texte(data?.erreur) || "La vérification a échoué.");
  };

  if (erreur) {
    return (
      <div style={CARTE}>
        {titre}
        <div role="status" style={CADRE}>{`La lecture des impressions a échoué : ${erreur}`}</div>
        <div style={{ marginTop: 12 }}>
          <button type="button" onClick={onRelire} style={BOUTON}>Recharger</button>
        </div>
      </div>
    );
  }

  if (chargement) {
    return <div style={CARTE}>{titre}<div role="status" style={AIDE}>Lecture en cours…</div></div>;
  }

  // Aucun lien : la commande est libre. C'est le seul etat ou l'on peut
  // commander, et c'est le cas de l'immense majorite des commandes.
  if (!impression) {
    return (
      <div style={CARTE}>
        {titre}
        <button
          type="button"
          onClick={onOuvrir}
          title="Préparer la commande d'impression chez Print.com"
          style={BOUTON_PLEIN}
        >
          Commander l'impression
        </button>
      </div>
    );
  }

  const etat = texte(impression.etat);
  const numero = texte(impression.numero_printcom);
  const nb = Number(nbCommandes) > 0 ? Number(nbCommandes) : 0;
  // Un panier, c'est une impression qui couvre plus d'une commande. Le dire est
  // obligatoire : sans ca, Simon croirait abandonner une seule commande.
  const estPanier = nb > 1;
  const ligneCompte = estPanier ? `Impression de ${compteCommandes(nb)}` : "";
  const etatLisible = ETATS[etat] ?? etat;
  const avecCompte = (phrase) => (estPanier ? `${ligneCompte} · ${phrase}` : phrase);

  // 🔴 LES TROIS SORTIES DE L'ENFERMEMENT (section 3 bis de la spec). Sans
  // elles, un panier en echec verrouille ses commandes POUR TOUJOURS : rien
  // d'autre dans le site ne relache ce verrou.
  //
  // La condition de l'abandon n'est PAS « etat echec » tout court : une
  // impression dont l'etat dit que les liens devraient avoir disparu, mais dont
  // les liens sont encore la, est REPARABLE. Ecrite par CONDITION et non en
  // enumerant un etat, elle ferme toute la famille ('annule' ET 'refuse') au
  // lieu d'un seul de ses membres.
  const liensDevraientAvoirDisparu = etat === "annule" || etat === "refuse";
  const peutReessayer = etat === "echec";
  const peutAbandonner = etat === "echec" || liensDevraientAvoirDisparu;
  // 🔴 Pas avant dix minutes, et jamais avec un numero : un envoi qui dure
  // trente secondes n'est pas une panne, et une impression deja partie n'a rien
  // a verifier.
  const peutVerifier = etat === "envoi_en_cours" && !numero && ageMs(impression) >= DELAI_RENVOI_MS;

  let corps;
  if (etat === "echec") {
    // En rouge, le motif MOT POUR MOT.
    corps = (
      <div role="alert" style={CADRE_ROUGE}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
          <Icone chemin={TRAIT_ALERTE} />
          <span>Échec de l'envoi</span>
        </div>
        {estPanier ? <div style={SUITE}>{ligneCompte}</div> : null}
        <div style={SUITE}>{texte(impression.motif) || "Aucun motif enregistré."}</div>
      </div>
    );
  } else if (liensDevraientAvoirDisparu) {
    // 🔴 JAMAIS « abandonnée » : ce serait faux pour un 'refuse'. La phrase dit
    // ce qui est vrai des deux : l'impression est terminee, le verrou ne l'est
    // pas.
    corps = (
      <div role="alert" style={CADRE_ROUGE}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
          <Icone chemin={TRAIT_ALERTE} />
          <span>Cette impression est terminée, mais ses commandes sont restées verrouillées.</span>
        </div>
        {estPanier ? <div style={SUITE}>{ligneCompte}</div> : null}
        {texte(impression.motif) ? <div style={SUITE}>{texte(impression.motif)}</div> : null}
      </div>
    );
  } else if (etat === "envoi_en_cours") {
    corps = (
      <div role="status" style={CADRE}>
        <div>{avecCompte(ETATS.envoi_en_cours)}</div>
        {numero ? <div style={SUITE}>{`Numéro Print.com : ${numero}`}</div> : null}
      </div>
    );
  } else {
    // 'commande', et les etats qui suivent (controle_fichier, en_production,
    // expedie, livre) : l'etat et le numero Print.com.
    corps = (
      <div role="status" style={CADRE}>
        <div style={{ fontWeight: 600 }}>{avecCompte(etatLisible)}</div>
        <div style={SUITE}>
          {numero ? `Numéro Print.com : ${numero}` : "Aucun numéro Print.com enregistré."}
        </div>
      </div>
    );
  }

  return (
    <div style={CARTE}>
      {titre}
      {corps}

      {(peutReessayer || peutAbandonner || peutVerifier) && (
        <div style={RANGEE_BOUTONS}>
          {peutReessayer && (
            <button
              type="button"
              onClick={onOuvrir}
              title="Rouvre le récapitulatif : rien ne repart sans un nouveau clic"
              style={BOUTON_PLEIN}
            >
              {estPanier ? `Réessayer l'impression des ${compteCommandes(nb)}` : "Réessayer"}
            </button>
          )}
          {peutAbandonner && (
            <button
              type="button"
              onClick={demanderAbandon}
              disabled={occupe}
              title="Relâche le verrou : les commandes redeviennent cochables"
              style={occupe ? BOUTON_ETEINT : BOUTON}
            >
              Abandonner cette impression
            </button>
          )}
          {peutVerifier && (
            <button
              type="button"
              onClick={verifierChezPrintCom}
              disabled={occupe}
              title="Renvoie exactement la même demande, pour que Print.com la reconnaisse"
              style={occupe ? BOUTON_ETEINT : BOUTON}
            >
              Vérifier chez Print.com
            </button>
          )}
        </div>
      )}

      {confirmation && (
        <div role="alert" style={CADRE_ROUGE}>
          <div>
            {texte(confirmation.appel_print_com) === "non" ? ABANDON_SANS_APPEL : ABANDON_DANGER}
          </div>
          <div style={RANGEE_BOUTONS}>
            <button
              type="button"
              onClick={confirmerAbandon}
              disabled={occupe}
              style={occupe ? BOUTON_ETEINT : BOUTON_PLEIN}
            >
              Abandonner définitivement
            </button>
            <button type="button" onClick={() => setConfirmation(null)} style={BOUTON}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {messageSortie ? <div role="alert" style={CADRE_ROUGE}>{messageSortie}</div> : null}
    </div>
  );
}

// ===========================================================================
// Le recapitulatif — rendu UNE SEULE FOIS, et seulement quand il est ouvert
// ===========================================================================

// Les 11 champs d'adresse figes par la tache 3. L'ordre est celui de la
// saisie. Les six marques « exige » sont ceux que Print.com reclame.
// L'ordre vient de Simon (2026-10-02) : c'est celui dans lequel il lit une
// adresse. Une LIGNE par rangee, et non une grille qui repartit toute seule :
// sinon l'ordre des champs depend de la largeur de la fenetre.
// `poids` fixe la part de la ligne : 1 pour un champ normal, moins pour les
// courts (numero, code postal, pays). A 375 px les lignes se replient toutes
// seules, un champ par ligne.
const LIGNES_ADRESSE = [
  [{ cle: "societe", label: "Société", aide: "Raison sociale sur l'étiquette", poids: 1 }],
  [{ cle: "nom", label: "Nom", aide: "Exigé par Print.com", poids: 1 },
   { cle: "prenom", label: "Prénom", aide: "Exigé par Print.com", poids: 1 }],
  [{ cle: "telephone", label: "Téléphone", aide: "Exigé par Print.com, pour le transporteur", poids: 1 },
   { cle: "email", label: "Email", aide: "Reçoit les avis de livraison", poids: 1 }],
  [{ cle: "numero_rue", label: "N°", aide: "Numéro dans la rue", poids: 0.4 },
   { cle: "rue", label: "Rue", aide: "Exigé par Print.com", poids: 1.3 },
   { cle: "complement", label: "Complément", aide: "Bâtiment, étage, porte", poids: 1.3 }],
  [{ cle: "code_postal", label: "Code postal", aide: "Code postal de livraison", poids: 0.7 },
   { cle: "ville", label: "Ville", aide: "Exigé par Print.com", poids: 1.3 },
   { cle: "pays", label: "Pays", aide: "Code pays à deux lettres, FR pour la France", poids: 0.4 }],
];

// Les 11 cles, a plat : la porte les attend TOUTES, et le repli se fait par cle
// absente. Derivee des lignes pour qu'aucune ne puisse etre oubliee en les
// reordonnant.
const CHAMPS_ADRESSE = LIGNES_ADRESSE.flat();

// Pre-remplissage depuis la commande, exactement comme le repli de la porte.
// ⚠️ On envoie ENSUITE les 11 cles, toujours : le repli de la porte se fait par
// cle ABSENTE (hasOwnProperty). Si Simon VIDE un champ, il doit voir le refus,
// pas un champ silencieusement re-rempli depuis la commande.
// D'ou vient chaque cle dans la commande. Le pays n'y existe pas : FR, comme la
// porte (section 8).
const SOURCE_ADRESSE = {
  societe: (c) => texte(c?.client_societe),
  prenom: (c) => texte(c?.client_prenom),
  nom: (c) => texte(c?.client_nom),
  numero_rue: (c) => texte(c?.numero_rue),
  rue: (c) => texte(c?.adresse1),
  complement: (c) => texte(c?.adresse2),
  code_postal: (c) => texte(c?.code_postal),
  ville: (c) => texte(c?.ville),
  pays: () => "FR",
  telephone: (c) => texte(c?.client_telephone),
  email: (c) => texte(c?.client_email),
};

function adresseDeLaCommande(commande) {
  // Construit a partir de CHAMPS_ADRESSE, et non d'une liste recopiee : ajouter
  // un champ aux lignes sans lui donner de source leverait ici, au lieu de
  // laisser passer une cle absente que la porte re-remplirait en silence.
  const adresse = {};
  for (const { cle } of CHAMPS_ADRESSE) {
    const source = SOURCE_ADRESSE[cle];
    if (!source) throw new Error(`Champ d'adresse sans source : ${cle}`);
    adresse[cle] = source(commande);
  }
  return adresse;
}

// Les plans finaux, quel que soit le nom de la cle : plansFinalises vient des
// ecrans (LayoutPrincipal les renomme), plans_finalises de la base.
function finalisesDe(commande) {
  if (Array.isArray(commande?.plansFinalises)) return commande.plansFinalises;
  if (Array.isArray(commande?.plans_finalises)) return commande.plans_finalises;
  return [];
}

// Une ligne de plan : son nom, son fichier final, et son orientation
// MODIFIABLE. Aucun hook : ce composant est rendu en boucle.
function LignePlan({ nom, plan, fichier, onVoir, onOrientation, bloque }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap", padding: "8px 0", borderTop: "1px solid #E5E7EB" }}>
      <div style={{ flex: "1 1 160px", minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", overflowWrap: "anywhere" }}>{nom}</div>
        <div style={AIDE}>{`${texte(plan?.type) || "—"} · ${texte(plan?.format) || "—"} · ${texte(plan?.matiere) || "—"}`}</div>
      </div>
      <label style={{ flex: "0 1 150px" }} title="Sens d'impression. Horizontale = paysage, Verticale = portrait.">
        <span style={LIBELLE}>Orientation</span>
        <select
          aria-label={`Orientation — ${nom}`}
          value={texte(plan?.orientation)}
          disabled={bloque}
          onChange={(e) => onOrientation(e.target.value)}
          style={CHAMP}
        >
          {ORIENTATIONS.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
        </select>
      </label>
      <div style={{ flex: "0 0 auto" }}>
        {fichier
          ? <button type="button" onClick={() => onVoir(fichier)} title={fichier.nom} style={BOUTON}>Voir</button>
          : <span style={AIDE}>Pas de plan final</span>}
      </div>
    </div>
  );
}

// Le prix d'un groupe ou d'un accessoire. prix_erreur est le message de
// Print.com, MOT POUR MOT : c'est lui qui apprend quelles options son produit
// attend. Jamais « une erreur est survenue ».
function Prix({ prix, erreur }) {
  if (typeof prix === "number") return <span>{eurosHT(prix)}</span>;
  return (
    <span style={{ color: ROUGE }}>
      {erreur ? `Pas de prix — ${erreur}` : "Pas de prix"}
    </span>
  );
}

// Un prix chiffre tient a droite, sur une ligne. Un message de Print.com fait
// trois lignes : aligne a droite il serait illisible. Il prend donc toute la
// largeur, a gauche.
function cadrePrix(prix) {
  return typeof prix === "number"
    ? { flex: "0 0 auto", textAlign: "right" }
    : { flex: "1 1 100%", textAlign: "left" };
}

function BlocGroupe({ groupe, plans, finalises, prefixe, onVoir, onOrientation, bloque }) {
  const options = groupe?.options && typeof groupe.options === "object" ? groupe.options : {};
  const accessoire = groupe?.accessoire ?? null;
  const livraison = groupe?.livraison ?? null;
  const lignes = Array.isArray(groupe?.plans) ? groupe.plans : [];
  return (
    <div style={{ border: "1px solid #E5E7EB", borderRadius: 12, padding: 12, marginTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", overflowWrap: "anywhere" }}>
            {`${texte(groupe?.matiere)} · ${texte(groupe?.format)} · ${texte(groupe?.orientation)}`}
          </div>
          <div style={AIDE} title="Référence du produit chez Print.com, et dimensions envoyées">
            {`${texte(groupe?.nb_plans)} plan(s) · ${texte(groupe?.sku) || "sans SKU"} · ${texte(options.width)} × ${texte(options.height)} mm`}
          </div>
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, ...cadrePrix(groupe?.prix) }}>
          <Prix prix={groupe?.prix} erreur={texte(groupe?.prix_erreur)} />
        </div>
      </div>

      {accessoire && (
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
          <div style={{ fontSize: 13, color: "#374151", overflowWrap: "anywhere" }}>
            {`Accessoire ${texte(accessoire.sku)} × ${texte(accessoire.copies)}`}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, ...cadrePrix(accessoire.prix) }}>
            <Prix prix={accessoire.prix} erreur={texte(accessoire.prix_erreur)} />
          </div>
        </div>
      )}

      {/* Les frais de livraison DE CE GROUPE. Un appel par groupe : c'est la
          raison du « au plus » du total quand il y a plusieurs groupes.
          Le transporteur et la date ne sont PAS repetes ici : ils sont annoncés
          une seule fois, dans le total. */}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
        <div style={{ fontSize: 13, color: "#374151" }}>Livraison</div>
        <div style={{ fontSize: 13, fontWeight: 600, ...cadrePrix(livraison?.prix) }}>
          <Prix prix={livraison?.prix} erreur={texte(groupe?.livraison_erreur)} />
        </div>
      </div>

      {/* ⚠️ CE QU'ON NE COMPREND PAS SE VOIT. Print.com annonce parfois un
          `price.urgency` chiffre sur la possibilite retenue, et leur
          documentation ne le decrit NULLE PART. Il n'est PAS ajoute au total :
          on l'affiche pour qu'on s'en apercoive au lieu de le perdre. Normal
          qu'il reste invisible : sur une livraison standard il vaut 0. */}
      {typeof livraison?.supplement_urgence === "number" && (
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
          <div style={{ fontSize: 13, color: "#374151" }} title="Annoncé par Print.com, non compté dans le total">
            Supplément urgence annoncé
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, flex: "0 0 auto", textAlign: "right" }}>
            {eurosHT(livraison.supplement_urgence)}
          </div>
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        {lignes.map((p) => (
          <LignePlan
            key={p.index}
            nom={nommerPlan(plans[p.index], p.index, prefixe)}
            plan={plans[p.index]}
            fichier={finalises.find((f) => f.plan_index === p.index) ?? null}
            onVoir={onVoir}
            onOrientation={(valeur) => onOrientation(p.index, valeur)}
            bloque={bloque}
          />
        ))}
      </div>
    </div>
  );
}

// Garde dans l'enveloppe, hooks dans le composant interne : c'est le motif de
// ReglagesImpression.js. react-hooks/rules-of-hooks est une ERREUR dans ce
// projet, et CI=true transforme les avertissements en erreurs.
//
// `commandes` est une LISTE (decision D1) : le detail d'une commande en passe
// une, le bandeau du panier en passe trois.
// `impressionId` : le reessai (D5). `nbCommandes` : combien de commandes
// l'impression couvre — un reessai s'ouvre depuis le detail d'UNE commande,
// l'ecran doit savoir des le depart qu'il s'agit d'un panier.
// `etatDevis` : le devis PRECHARGE par le parent. Absent (le panier), le
// recapitulatif possede son propre devis, qui part a son ouverture.
// OU LIVRER CETTE COMMANDE, et son adresse quand elle part chez son client.
//
// 🔴 UN SEUL COMPOSANT, utilise par le detail d'une commande ET par chaque bloc
// d'un panier. C'est ce qui garantit qu'une commande seule et une commande dans
// un panier se comportent EXACTEMENT pareil.
//
// `suffixe` nomme la commande dans les libelles pour les lecteurs d'ecran quand
// il y en a plusieurs ( « — FI-BETA-202 » ). Il reste VIDE pour une commande
// seule : le texte visible, lui, ne change jamais. Regle dure « zero texte
// explicatif » : les precisions vont en info-bulle, pas a l'ecran.
// Aucun hook ici : ce composant est rendu EN BOUCLE.
function ChoixDestination({ suffixe, destination, adresse, onDestination, onChamp, onQuitter }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => onDestination("local")}
          title="Livraison au local, pour grouper les envois"
          aria-label={`Livrer chez moi${suffixe}`}
          style={destination === "local" ? BOUTON_PLEIN : BOUTON}
        >
          Livrer chez moi
        </button>
        <button
          type="button"
          onClick={() => onDestination("client")}
          title="Livraison directe à l'adresse du client"
          aria-label={`Livrer chez le client${suffixe}`}
          style={destination === "client" ? BOUTON_PLEIN : BOUTON}
        >
          Livrer chez le client
        </button>
      </div>

      {destination === "client" && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
          {LIGNES_ADRESSE.map((ligne, i) => (
            <div key={i} style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {ligne.map(({ cle, label, aide, poids }) => (
                <label key={cle} style={{ flex: `${poids} 1 140px`, minWidth: 0 }} title={aide}>
                  <span style={LIBELLE}>{label}</span>
                  <input
                    type="text"
                    value={texte(adresse[cle])}
                    aria-label={suffixe ? `${label}${suffixe}` : undefined}
                    onChange={(e) => onChamp(cle, e.target.value)}
                    onBlur={onQuitter}
                    style={CHAMP}
                  />
                </label>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function RecapImpression({
  commandes, impressionId = null, nbCommandes = 0, etatDevis = null,
  onFermer, onImpressionMaj, onPlansMaj,
}) {
  const liste = Array.isArray(commandes) ? commandes.filter((c) => c && texte(c.id)) : [];
  if (liste.length === 0) return null;
  return (
    <Recap
      commandes={liste}
      impressionId={texte(impressionId).trim()}
      nbCommandes={Number(nbCommandes) > 0 ? Number(nbCommandes) : 0}
      etatDevis={etatDevis}
      onFermer={onFermer}
      onImpressionMaj={onImpressionMaj}
      onPlansMaj={onPlansMaj}
    />
  );
}

function Recap({ commandes, impressionId, nbCommandes, etatDevis, onFermer, onImpressionMaj, onPlansMaj }) {
  const ids = commandes.map((c) => texte(c.id));
  // La commande que l'ecran appelant connait. C'est la SEULE dont la correction
  // d'orientation peut etre remontee au parent : lui en remonter une autre
  // ecraserait les plans de celle qu'il affiche.
  const idPrincipal = ids[0];

  // Les plans tels que la BASE les connait, commande par commande. Toute
  // correction d'orientation est ecrite en base AVANT d'etre affichee ici : la
  // porte relit la base, elle ne verrait pas une correction restee a l'ecran.
  const [plansParCommande, setPlansParCommande] = useState(() => {
    const m = new Map();
    for (const c of commandes) m.set(texte(c.id), Array.isArray(c.plans) ? c.plans : []);
    return m;
  });
  // Le dossier d'affichage de chaque commande : sa reference, son client, ses
  // plans finaux.
  const [dossiers, setDossiers] = useState(() => {
    const m = new Map();
    for (const c of commandes) {
      m.set(texte(c.id), { id: texte(c.id), ref: texte(c.ref), nom: nomDuClient(c), finalises: finalisesDe(c) });
    }
    return m;
  });
  // 🔴 UNE DESTINATION ET UNE ADRESSE PAR COMMANDE. C'est le miroir, cote ecran,
  // du piege `commandesLues[0]` de la porte : une seule adresse pour tout le
  // recapitulatif enverrait les plans de TOUTES les commandes chez le PREMIER
  // client. La Map des destinations ne contient que les choix EXPLICITES ;
  // l'absence vaut « chez moi », la destination qui ne peut pas se tromper.
  const [destinations, setDestinations] = useState(() => new Map());
  const [adresses, setAdresses] = useState(() => {
    const m = new Map();
    for (const c of commandes) m.set(texte(c.id), adresseDeLaCommande(c));
    return m;
  });
  // Une adresse en cours de frappe : le devis affiche ne correspond plus. Le
  // bouton disparait jusqu'au nouveau devis. Un devis perime ferait approuver
  // autre chose que ce qui part.
  // 🔴 UN ENSEMBLE, PAS UN BOOLEEN : quitter le champ d'UNE commande ne doit pas
  // effacer le drapeau d'une AUTRE dont l'adresse est encore en cours de frappe.
  const [perimes, setPerimes] = useState(() => new Set());
  const [plafond, setPlafond] = useState(null);
  const [aConfirme, setAConfirme] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [resultat, setResultat] = useState(null);
  const [erreurPlans, setErreurPlans] = useState("");
  const [visu, setVisu] = useState(null);

  // Le devis. Precharge par DetailCommandeModal dans le detail d'une commande ;
  // possede ici quand c'est le bandeau du panier qui ouvre (section 4.1 : le
  // devis part au clic, il n'y a pas de prechargement pour un panier).
  // 🪤 Le hook est appele TOUJOURS — jamais sous condition — et simplement
  // inerte quand le parent fournit deja son etat.
  const interne = useDevisImpression(commandes, !etatDevis, impressionId);
  const { devis, attente, refusPorte, assurer } = etatDevis ?? interne;

  // A l'ouverture : on s'ASSURE qu'un devis existe. Apres un prechargement
  // reussi c'est immediat et AUCUN appel ne part. Apres un prechargement rate,
  // c'est ici que la demande est refaite — et c'est seulement maintenant que
  // Simon voit l'echec. Pour un panier, c'est l'unique depart du devis.
  useEffect(() => {
    // A l'ouverture, TOUTES les commandes connues partent « chez moi » (E4). Un
    // reessai, lui, n'en connait encore aucune : on n'envoie donc pas de liste,
    // et la porte applique sa propre liste de liens, chez moi elle aussi.
    assurer(impressionId ? [] : ids.map((id) => ({ id, destination: "local" })), plansParCommande);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Un devis different, c'est un montant different : la confirmation du plafond
  // ne vaut plus. C'etait deja le cas avant, quand le devis vivait ici.
  useEffect(() => { setAConfirme(false); }, [devis]);

  // Le plafond vit dans reglages_impression, pas dans la reponse de la porte.
  useEffect(() => {
    let vivant = true;
    (async () => {
      const { data } = await supabase
        .from("reglages_impression").select("plafond_ttc").eq("id", true).single();
      if (!vivant) return;
      const v = Number(data?.plafond_ttc);
      setPlafond(Number.isFinite(v) && v > 0 ? v : null);
    })();
    return () => { vivant = false; };
  }, []);

  const groupes = Array.isArray(devis?.groupes) ? devis.groupes : [];
  const refus = Array.isArray(devis?.refus) ? devis.refus : [];
  const total = devis?.total;
  // Les commandes que le devis nomme, dans l'ordre ou elles apparaissent.
  const idsDuDevis = [...new Set(groupes.map((g) => texte(g?.commande_id)).filter((id) => id))];
  // 🔴 LES COMMANDES QUE LA PORTE A NOMMEES. Sur un reessai c'est ELLE qui designe
  // l'ensemble (D5), et elle refuse une liste qui ne serait pas exactement celle
  // de ses liens. On ne la devine donc jamais : on la lit dans sa reponse.
  // `destinations` est renvoye MEME quand il n'y a aucun groupe (un refus
  // d'adresse n'en produit aucun) : le repli sur les groupes ne sert qu'aux
  // reponses d'avant la 2.23.0.
  const idsAnnonces = Array.isArray(devis?.destinations)
    ? [...new Set(devis.destinations.map((d) => texte(d?.commande_id)).filter((id) => id))]
    : idsDuDevis;
  // 🔴 ET ON NE LES OUBLIE PLUS. Pendant un recalcul `devis` repasse a null : sans
  // cette memoire, un deuxieme choix de destination partirait SANS liste, et la
  // porte remettrait silencieusement TOUT « chez moi ».
  const idsConnus = useRef([]);
  if (idsAnnonces.length > 0) idsConnus.current = idsAnnonces;
  const idsDeLaPorte = idsAnnonces.length > 0 ? idsAnnonces : idsConnus.current;
  // Les commandes que cet ecran montre : celles que la porte nomme, plus celles
  // qu'il connaissait deja en s'ouvrant.
  const idsAffiches = idsDeLaPorte.length > 0 ? [...new Set([...idsDeLaPorte, ...ids])] : ids;
  // Une impression qui couvre plus d'une commande EST un panier. On le sait
  // avant meme le devis quand le bloc nous l'a dit (un reessai), et par le
  // devis sinon.
  const estPanier = Math.max(ids.length, nbCommandes, idsAffiches.length) > 1;
  // Celles que la porte doit entendre. Pour un reessai : SEULEMENT les siennes.
  const idsPourLaPorte = impressionId ? idsDeLaPorte : ids;
  // 🔴 UNE SEULE FACON D'ALLER CHERCHER L'ADRESSE D'UNE COMMANDE, parce que
  // l'ecran l'AFFICHE et l'ENVOIE. Deux chemins differents, et il pourrait un
  // jour montrer une adresse et en envoyer une autre : Simon approuverait alors
  // un recapitulatif qui ment, sur une commande QUI NE S'ANNULE PAS.
  const adresseDans = (adrs, id) => adrs.get(id) ?? adresseDeLaCommande(null);
  const adresseDe = (id) => adresseDans(adresses, id);
  const destinationDe = (id) => destinations.get(id) ?? "local";
  // Le corps que la porte attend, construit a partir d'un etat donne : on le
  // fabrique AVEC la valeur qu'on vient de poser, jamais avec celle du rendu
  // precedent, que React n'a pas encore remplacee.
  const choixPour = (dests, adrs) => idsPourLaPorte.map((id) => (
    (dests.get(id) ?? "local") === "client"
      ? { id, destination: "client", adresse: adresseDans(adrs, id) }
      : { id, destination: "local" }));

  // 🧺 LES COMMANDES DU PANIER QUE CET ECRAN NE CONNAIT PAS. Un reessai s'ouvre
  // depuis le detail d'UNE commande, et la porte en rend trois : sans cette
  // relecture, les plans des deux autres s'afficheraient vides, et Simon
  // approuverait un recapitulatif qui ne montre pas tout ce qui part.
  const cleDesGroupes = idsAffiches.join(",");
  useEffect(() => {
    const manquants = idsAffiches.filter((id) => !dossiers.has(id));
    if (manquants.length === 0) return undefined;
    let vivant = true;
    (async () => {
      const { data } = await supabase
        .from("commandes")
        // 🔴 LES COLONNES D'ADRESSE SONT INDISPENSABLES ICI. Sans elles, une
        // commande decouverte par un reessai aurait un formulaire d'adresse VIDE,
        // et « livrer chez le client » partirait sans destinataire.
        .select("id, ref, plans, plans_finalises, client_societe, client_prenom, client_nom, client_email, client_telephone, numero_rue, adresse1, adresse2, code_postal, ville")
        .in("id", manquants);
      if (!vivant || !Array.isArray(data) || data.length === 0) return;
      setDossiers((avant) => {
        const suite = new Map(avant);
        for (const c of data) {
          suite.set(texte(c.id), { id: texte(c.id), ref: texte(c.ref), nom: nomDuClient(c), finalises: finalisesDe(c) });
        }
        return suite;
      });
      setPlansParCommande((avant) => {
        const suite = new Map(avant);
        for (const c of data) suite.set(texte(c.id), Array.isArray(c.plans) ? c.plans : []);
        return suite;
      });
      setAdresses((avant) => {
        const suite = new Map(avant);
        // 🪤 `if (!suite.has(id))` : ne JAMAIS ecraser ce que Simon vient de taper.
        for (const c of data) {
          const id = texte(c.id);
          if (!suite.has(id)) suite.set(id, adresseDeLaCommande(c));
        }
        return suite;
      });
    })();
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleDesGroupes]);

  // Basculer « chez moi » / « chez le client » POUR UNE COMMANDE : le premier
  // passage demande un devis, le deuxieme le retrouve en memoire.
  const changerDestination = (id, dest) => {
    const suite = new Map(destinations);
    suite.set(id, dest);
    setDestinations(suite);
    // Repasser cette commande « chez moi » rend sa frappe en cours sans objet :
    // son adresse n'est plus lue. Les AUTRES gardent leur drapeau.
    const restants = new Set(perimes);
    restants.delete(id);
    setPerimes(restants);
    assurer(choixPour(suite, adresses), plansParCommande);
  };

  const changerChamp = (id, cle, valeur) => {
    setAdresses((a) => {
      const suite = new Map(a);
      suite.set(id, { ...(suite.get(id) ?? adresseDeLaCommande(null)), [cle]: valeur });
      return suite;
    });
    setPerimes((p) => new Set(p).add(id));
  };

  // Un devis par frappe de clavier ferait un appel Print.com par lettre : on
  // redemande quand le champ est quitte, et jusque-la le bouton reste eteint.
  // 🔴 SEUL LE DRAPEAU DE CETTE COMMANDE TOMBE. Une autre dont l'adresse est
  // encore a moitie tapee garde le sien, et le bouton reste eteint pour tous.
  const quitterChamp = (id) => {
    if (!perimes.has(id)) return;
    const restants = new Set(perimes);
    restants.delete(id);
    setPerimes(restants);
    assurer(choixPour(destinations, adresses), plansParCommande);
  };

  // ⚠️ L'orientation corrigee est ECRITE EN BASE par le navigateur, puis le
  // devis est redemande : une orientation modifiee re-decoupe les groupes et
  // change les prix.
  const changerOrientation = async (commandeId, index, valeur) => {
    const anciens = plansParCommande.get(commandeId) ?? [];
    const nouveaux = anciens.map((p, i) => (i === index ? { ...(p ?? {}), orientation: valeur } : p));
    setErreurPlans("");
    const { error } = await supabase.from("commandes").update({ plans: nouveaux }).eq("id", commandeId);
    if (error) {
      // Rien n'a ete ecrit : on ne fait PAS semblant a l'ecran, et on ne
      // redemande pas un devis qui porterait sur l'ancienne valeur.
      setErreurPlans(`L'orientation n'a pas pu être enregistrée : ${texte(error.message)}`);
      return;
    }
    const suite = new Map(plansParCommande);
    suite.set(commandeId, nouveaux);
    setPlansParCommande(suite);
    // Seule la commande que le parent affiche peut lui etre remontee.
    if (commandeId === idPrincipal) onPlansMaj?.(nouveaux);
    // L'orientation fait partie de la cle de la memoire : ce devis-la n'a jamais
    // ete demande, il part donc vraiment.
    assurer(choixPour(destinations, adresses), suite);
  };

  // La livraison, telle que la porte l'a calculée. L'écran ne fait que la mettre
  // en forme : « Livraison · DHL · 08/10/2026 ». Le transporteur et la date sont
  // ceux de la porte, pas une déduction d'ici.
  const livraisonTotale = devis?.livraison_totale;
  const transporteurs = Array.isArray(devis?.livraison_transporteurs) ? devis.livraison_transporteurs : [];
  const libelleLivraison = ["Livraison", transporteurs.map(texte).filter((t) => t).join(" · "), dateFr(devis?.livraison_date)]
    .filter((bout) => bout)
    .join(" · ");
  const prixComplet = devis?.ok === true && typeof total === "number";
  // Ni devis ni refus : c'est qu'on attend. Le prechargement part dans un effet,
  // donc juste apres le montage l'etat est encore vide : sans ca, « Pas de prix »
  // clignoterait avant le « Demande du devis en cours… ».
  // Vrai des qu'un devis (ou un refus) a ete affiche une fois. Un ref et non
  // un etat : le changer ne doit pas provoquer de nouveau rendu.
  const dejaVu = useRef(false);
  const enAttente = attente || (!devis && !refusPorte);
  // PREMIER chargement seulement. Une nouvelle demande remet le devis a zero :
  // sans cette distinction, cliquer « Livrer chez le client » faisait
  // DISPARAITRE les boutons et le formulaire d'adresse pendant le recalcul. On
  // cache tant qu'il n'y a jamais rien eu a montrer ; ensuite on garde les
  // commandes a l'ecran et seul le total s'efface.
  if (devis || refusPorte) dejaVu.current = true;
  const premierChargement = enAttente && !dejaVu.current;
  // Pas de prix → pas de bouton. Un refus → pas de bouton non plus : la porte
  // s'arreterait de toute facon avant d'appeler Print.com.
  const peutCommander = prixComplet && refus.length === 0 && perimes.size === 0 && !enAttente && !resultat?.ok;
  const auDessusDuPlafond = prixComplet && plafond !== null && total > plafond;

  // 🔴 « UN PANIER VA TOUJOURS CHEZ SIMON » EST MORT (decision E3). On peut
  // desormais tout melanger : trois commandes chez Simon et deux chez leurs
  // clients, dans le MEME envoi. Les trois premieres partagent leurs frais
  // d'expedition, les deux autres paient le leur.

  const commander = async () => {
    setEnvoi(true);
    setResultat(null);
    // 🔴 Un reessai est designe par son IMPRESSION, jamais par le panier
    // courant : l'ensemble des commandes ne change JAMAIS sur un reessai (D5).
    // Les destinations, elles, sont celles du recapitulatif qu'il vient
    // d'approuver : une adresse corrigee a la main ne vit nulle part ailleurs.
    const corps = impressionId
      ? { action: "commander", impression_id: impressionId }
      : { action: "commander" };
    const liste = choixPour(destinations, adresses);
    if (liste.length > 0) corps.commandes = liste;
    let reponse;
    try {
      reponse = await supabase.functions.invoke("print-commander", { body: corps });
    } catch (e) {
      reponse = { data: null, error: e };
    }
    setEnvoi(false);
    const { data, error } = reponse ?? {};
    if (!data) {
      setResultat({ ok: false, erreur: texte(error?.message) || "Réponse vide de print-commander.", porte: true });
    } else {
      setResultat(data);
    }
    // Que l'envoi ait abouti ou echoue, la ligne d'impression a pu bouger :
    // le bloc doit la relire.
    onImpressionMaj?.(data);
  };

  // Les groupes, RANGES PAR COMMANDE. C'est ce que la decision 4.2 achete :
  // une ligne Print.com par client, avec son nom. Il faut donc le montrer.
  const parCommande = [];
  const indexParCommande = new Map();
  for (const g of groupes) {
    const id = texte(g?.commande_id) || (ids.length === 1 ? idPrincipal : "");
    if (!indexParCommande.has(id)) {
      const dossier = dossiers.get(id) ?? null;
      indexParCommande.set(id, parCommande.length);
      parCommande.push({
        id,
        ref: texte(g?.commande_ref) || texte(dossier?.ref),
        nom: texte(g?.nom_client) || texte(dossier?.nom),
        groupes: [],
      });
    }
    parCommande[indexParCommande.get(id)].groupes.push(g);
  }

  const plansDe = (id) => plansParCommande.get(id) ?? [];
  const finalisesDuDossier = (id) => dossiers.get(id)?.finalises ?? [];
  // Dans un panier, chaque plan porte la reference de sa commande : sans elle,
  // « Plan 1 (Hall) » existerait trois fois a l'ecran.
  const prefixeDe = (id) => (estPanier ? (texte(dossiers.get(id)?.ref) || texte(id)) : "");

  // 🔴 UN BLOC PAR COMMANDE AFFICHEE, et non par commande qui a des groupes. Une
  // adresse incomplete ne rend AUCUN groupe : sans ca, la commande fautive
  // disparaitrait de l'ecran, et Simon n'aurait plus nulle part ou corriger la
  // destination qui a provoque le refus.
  const blocsAffiches = idsAffiches.map((id) => {
    const rang = indexParCommande.get(id);
    const duDevis = rang === undefined ? null : parCommande[rang];
    const dossier = dossiers.get(id) ?? null;
    return {
      id,
      ref: texte(duDevis?.ref) || texte(dossier?.ref) || id,
      nom: texte(duDevis?.nom) || texte(dossier?.nom),
      groupes: duDevis?.groupes ?? [],
    };
  });

  const OVERLAY = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 600,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 12,
  };
  const BOITE = {
    background: "#fff", borderRadius: 14, padding: 20, width: "min(680px, 100%)",
    maxHeight: "92vh", overflowY: "auto", boxSizing: "border-box",
  };

  return (
    <>
      {visu && <VisuFichier fichier={visu} onClose={() => setVisu(null)} />}
      <div style={OVERLAY} onClick={(e) => e.target === e.currentTarget && onFermer()}>
        <div style={BOITE} role="dialog" aria-label="Commander l'impression">

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Commander l'impression</div>
            <button type="button" onClick={onFermer} style={BOUTON}>Fermer</button>
          </div>

          {/* Le terrain d'essai, dit par la porte elle-meme. */}
          {devis?.environnement === "essai" && (
            <div role="status" style={{ ...CADRE, fontWeight: 700 }}>
              TERRAIN D'ESSAI — rien ne sera imprimé
            </div>
          )}

          {enAttente && (
            <div role="status" style={{ ...CADRE, display: "flex", alignItems: "center", gap: 10 }}>
              <svg className="ip-tourne" width="18" height="18" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M12 3a9 9 0 1 0 9 9" strokeLinecap="round" />
              </svg>
              <span>Calcul du prix en cours…</span>
            </div>
          )}

          {/* invoke() a resolu avec data: null : la porte a refuse au niveau du
              protocole. On garde son message, et on dit ce qu'il veut dire. */}
          {refusPorte && (
            <div role="alert" style={CADRE_ROUGE}>
              <div>{refusPorte}</div>
              <div style={SUITE}>Refus de print-commander : droits insuffisants ou session expirée.</div>
            </div>
          )}

          {/* ok: false — le message de la porte, et son detail s'il existe. */}
          {devis && devis.ok !== true && (
            <div role="alert" style={CADRE_ROUGE}>
              <div>{texte(devis.erreur) || "La porte a refusé la demande."}</div>
              {devis.detail
                ? <div style={SUITE}>{typeof devis.detail === "string" ? devis.detail : JSON.stringify(devis.detail)}</div>
                : null}
            </div>
          )}

          {/* Les refus, en clair, en nommant la commande, le plan ou le champ
              fautif. La porte prefixe elle-meme par la reference quand le panier
              porte plusieurs commandes (decision D3). */}
          {refus.length > 0 && (
            <div role="alert" style={CADRE_ROUGE}>
              {refus.map((r, i) => <div key={i} style={i === 0 ? undefined : SUITE}>{texte(r)}</div>)}
            </div>
          )}

          {erreurPlans && <div role="alert" style={CADRE_ROUGE}>{erreurPlans}</div>}

          {/* Un bloc par commande : son nom, ses groupes, et OU ELLE PART. Le
              nom du client n'apparait que dans un panier : dans le detail d'une
              commande, Simon sait deja laquelle il regarde. */}
          {blocsAffiches.map((bloc) => (
            <div key={bloc.id || "sans-commande"}>
              {estPanier && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", overflowWrap: "anywhere" }}>
                    {bloc.nom || bloc.ref || bloc.id}
                  </div>
                  <div style={AIDE}>{bloc.ref || bloc.id}</div>
                </div>
              )}
              {bloc.groupes.map((g, i) => (
                <BlocGroupe
                  key={`${texte(g.matiere)}|${texte(g.format)}|${texte(g.orientation)}|${i}`}
                  groupe={g}
                  plans={plansDe(bloc.id)}
                  finalises={finalisesDuDossier(bloc.id)}
                  prefixe={prefixeDe(bloc.id)}
                  onVoir={setVisu}
                  onOrientation={(index, valeur) => changerOrientation(bloc.id, index, valeur)}
                  bloque={envoi}
                />
              ))}

              {/* Aucun groupe pour CETTE commande (un refus, et la porte n'en
                  renvoie aucun) : l'orientation reste corrigeable, sinon le refus
                  « orientation non renseignée » serait sans issue. */}
              {devis && bloc.groupes.length === 0 && plansDe(bloc.id).length > 0 && (
                <div style={{ border: "1px solid #E5E7EB", borderRadius: 12, padding: 12, marginTop: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                    {estPanier ? (bloc.nom || bloc.ref) : "Plans"}
                  </div>
                  {plansDe(bloc.id).map((p, i) => (
                    <LignePlan
                      key={i}
                      nom={nommerPlan(p, i, prefixeDe(bloc.id))}
                      plan={p}
                      fichier={finalisesDuDossier(bloc.id).find((fi) => fi.plan_index === i) ?? null}
                      onVoir={setVisu}
                      onOrientation={(valeur) => changerOrientation(bloc.id, i, valeur)}
                      bloque={envoi}
                    />
                  ))}
                </div>
              )}

              {/* 🔴 OU LIVRER CETTE COMMANDE-LA. La garde « jamais pour un panier »
                  est MORTE (decision E3) : c'est tout l'objet de ce chantier. Reste
                  masque pendant le PREMIER calcul : Simon ne doit pas choisir une
                  destination sur un ecran qui n'a pas encore ses chiffres, le devis
                  repartirait aussitot. */}
              {!premierChargement && (
                <ChoixDestination
                  suffixe={estPanier ? ` — ${bloc.ref}` : ""}
                  destination={destinationDe(bloc.id)}
                  adresse={adresseDe(bloc.id)}
                  onDestination={(dest) => changerDestination(bloc.id, dest)}
                  onChamp={(cle, valeur) => changerChamp(bloc.id, cle, valeur)}
                  onQuitter={() => quitterChamp(bloc.id)}
                />
              )}
            </div>
          ))}

          {/* Le total, frais de livraison COMPRIS. La mention « hors frais
              d'expédition » a disparu : elle n'est plus vraie. Masque pendant
              l'attente : « Pas de prix » en rouge sous un chargement en cours
              ferait croire a un echec. */}
          {!enAttente && (
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: "2px solid #E5E7EB" }}>
            {devis && (
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
                <span style={{ fontSize: 13, color: "#374151", overflowWrap: "anywhere" }}>{libelleLivraison}</span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {typeof livraisonTotale === "number"
                    ? eurosHT(livraisonTotale)
                    : <span style={{ color: ROUGE }}>Pas de prix</span>}
                </span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <span style={{ fontSize: 14, fontWeight: 700 }}>Total</span>
              <span style={{ fontSize: 14, fontWeight: 700 }}>
                {prixComplet ? eurosHT(total) : <span style={{ color: ROUGE }}>Pas de prix</span>}
              </span>
            </div>
            {devis && <div style={{ ...AIDE, marginTop: 4 }}>{MENTION_TAXE}</div>}
            {/* 🔴 Plusieurs groupes pour la même adresse : la porte a compté un
                envoi PAR GROUPE, et elle le dit. Avec un panier il y a au moins
                un groupe par commande : la somme sur-estime largement, puisque
                Print.com regroupe les envois identiques tout seul. « Au plus »
                n'est pas une précaution de style : c'est la seule chose vraie
                qu'on sache. 🚫 Aucune remise n'est calculée ni devinée. */}
            {devis?.livraison_estimation_haute === true && (
              <div style={{ ...AIDE, marginTop: 4 }}>
                {`Livraison : au plus ${eurosHT(livraisonTotale)} — Print.com regroupe les envois identiques, la facture sera souvent plus basse.`}
              </div>
            )}
          </div>
          )}

          {/* Le devis ne correspond plus a ce qui est a l'ecran. Dans un panier,
              on NOMME les commandes concernees : sans ca, Simon chercherait
              laquelle des cinq adresses il a laissee a moitie tapee. */}
          {perimes.size > 0 && (
            <div role="status" style={CADRE}>
              <div>L'adresse a changé : le devis doit être refait.</div>
              {estPanier && (
                <div style={SUITE}>
                  {[...perimes].map((id) => texte(dossiers.get(id)?.ref) || id).join(" · ")}
                </div>
              )}
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => { setPerimes(new Set()); assurer(choixPour(destinations, adresses), plansParCommande); }}
                  style={BOUTON}
                >
                  Refaire le devis
                </button>
              </div>
            </div>
          )}

          {/* Le resultat de l'envoi. */}
          {resultat && (
            resultat.ok === true ? (
              <div role="status" style={CADRE}>
                <div style={{ fontWeight: 700 }}>Commande envoyée</div>
                <div style={SUITE}>{`Numéro Print.com : ${texte(resultat.numero_printcom)}`}</div>
              </div>
            ) : (
              <div role="alert" style={CADRE_ROUGE}>
                <div>{texte(resultat.erreur) || "Print.com a refusé la commande."}</div>
                {resultat.detail
                  ? <div style={SUITE}>{typeof resultat.detail === "string" ? resultat.detail : JSON.stringify(resultat.detail)}</div>
                  : null}
                {resultat.porte
                  ? <div style={SUITE}>Refus de print-commander : droits insuffisants ou session expirée.</div>
                  : null}
                {Array.isArray(resultat.refus)
                  ? resultat.refus.map((r, i) => <div key={i} style={SUITE}>{texte(r)}</div>)
                  : null}
              </div>
            )
          )}

          {/* Le bouton. Pas de prix, un refus, un devis perime, un envoi en
              cours : pas de bouton, ou un bouton eteint. */}
          <div style={RANGEE_BOUTONS}>
            {peutCommander && !auDessusDuPlafond && (
              <button type="button" onClick={commander} disabled={envoi} style={envoi ? BOUTON_ETEINT : BOUTON_PLEIN}>
                {envoi ? "Envoi en cours…" : "Confirmer et commander"}
              </button>
            )}

            {/* Au-dessus du plafond : une 2e confirmation. */}
            {peutCommander && auDessusDuPlafond && !aConfirme && (
              <>
                <span role="alert" style={{ fontSize: 13, color: ROUGE }}>
                  {`${eurosHT(total)} dépasse le plafond de ${euros(plafond)}.`}
                </span>
                <button type="button" onClick={() => setAConfirme(true)} style={BOUTON}>
                  Je confirme le montant
                </button>
              </>
            )}
            {peutCommander && auDessusDuPlafond && aConfirme && (
              <button type="button" onClick={commander} disabled={envoi} style={envoi ? BOUTON_ETEINT : BOUTON_PLEIN}>
                {envoi ? "Envoi en cours…" : "Confirmer et commander"}
              </button>
            )}
          </div>

        </div>
      </div>
    </>
  );
}
